import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Fragment } from 'react';

import ScheduleIcon from '@/assets/icons/ic-schedule.svg';

import { FontFamily } from '@/enums/index';
import { BREAKPOINTS, theme as customTheme } from '@/styles/themes/default';

import { slideToLeft, delayedAnimation, Card } from './shared';

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: 268px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    max-width: 400px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    max-width: 594px;
    height: 370px;
  }
`;

const Separator = styled.div`
  height: 320px;
  min-width: 1px;
  margin-right: 118px;
  background: #cccccc;
`;

const Time = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin-bottom: 2px;

  span {
    width: 65px;
    font-family: ${FontFamily.IBMPlexSans};
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    color: #808080;
    margin-right: 64px;
  }
`;

const Grid = styled.div`
  display: flex;

  margin-left: 32px;
`;

const CalendarGrid = () => {
  return (
    <Fragment>
      <Time>
        <span>13:00</span>
        <span>14:00</span>
        <span>15:00</span>
        <span>16:00</span>
        <span>17:00</span>
      </Time>
      <Grid>
        <Separator />
        <Separator />
        <Separator />
        <Separator />
        <Separator />
      </Grid>
    </Fragment>
  );
};

const ItemBox = styled.div<{ boxTheme: any }>`
  position: absolute;
  left: 100%;
  background: ${({ boxTheme }) => boxTheme.primary};
  border-radius: 8px;
  box-shadow: 0px 0px 24px rgba(0, 0, 0, 0.12);
  padding: 10px 9px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    padding: 12px 9px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    padding: 20px 16px;
  }

  span {
    font-family: ${FontFamily.SpaceGrotesk};
    font-weight: bold;
    font-size: 9px;
    line-height: 10px;
    color: ${({ boxTheme }) => boxTheme.text};

    :after {
      display: block;
      content: '';
      margin-top: 7px;
      width: 74px;
      height: 9px;
      background: ${({ boxTheme }) => boxTheme.secondary};
      border-radius: 14px;
    }

    @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
      font-size: 16px;
      line-height: 18px;

      :after {
        height: 16px;
        margin-top: 12px;
      }
    }
  }
`;

const calendarItem1Styles = css`
  top: 50px;
  width: 154px;
  margin-left: 35px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    width: 174px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    top: 40px;
    width: 280px;
  }
`;

const calendarItem2Styles = css`
  top: 140px;
  margin-left: 10px;
  width: 142px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    width: 160px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    top: 140px;
    width: 257px;
  }
`;

const calendarItem3Styles = css`
  top: 225px;
  margin-left: 50px;
  width: 210px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    width: 240px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    top: 240px;
    width: 382px;
  }
`;

const calendarItem4Styles = css`
  top: 50px;
  margin-left: 200px;
  width: 136px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    width: 153px;
    margin-left: 230px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    top: 40px;
    width: 247px;
    margin-left: 340px;
  }
`;

const calendarItem5Styles = css`
  top: 140px;
  width: 152px;
  margin-left: 170px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    width: 171px;
    margin-left: 190px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    top: 140px;
    width: 277px;
    margin-left: 290px;
  }
`;

const cardStyles = css`
  z-index: 2;
  left: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  color: ${customTheme.colors.brand.primary};
  padding: 10px 20px 10px 30px;
  min-width: 150px;
  top: 260px;
  margin-left: 190px;

  span {
    font-size: 9px;
    line-height: 11px;
    font-weight: bold;
  }

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    top: 270px;

    span {
      font-size: 12px;
      line-height: 16px;
    }
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    top: 300px;
    padding: 16px 20px 16px 32px;
    margin-left: 310px;

    span {
      font-size: 16px;
      line-height: 24px;
    }
  }
`;

const CalendarItem = (props) => {
  const { title, variant, style } = props;

  const theme =
    variant === 'light'
      ? {
          primary: '#EDE8FA',
          secondary: '#D0BFF9',
          text: ' #5B1CF4',
        }
      : {
          primary: '#5B1CF4',
          secondary: '#4B15CE',
          text: '#ffffff',
        };

  return (
    <ItemBox boxTheme={theme} css={style}>
      <span>{title}</span>
    </ItemBox>
  );
};

export const Schedule = () => {
  return (
    <Wrapper>
      <CalendarGrid
        css={css`
          width: 100%;
          height: 100%;
        `}
      />
      <CalendarItem title="Gender differences in behaviour" style={[calendarItem1Styles, slideToLeft]} />
      <CalendarItem
        title="Frontier developments in AI"
        style={[calendarItem2Styles, slideToLeft, delayedAnimation(0.7)]}
      />
      <CalendarItem
        title="Preventing failure in crisis managment"
        style={[calendarItem3Styles, slideToLeft, delayedAnimation(1.1)]}
      />
      <CalendarItem
        title="Rethinking tech in science"
        variant="light"
        style={[calendarItem4Styles, slideToLeft, delayedAnimation(1.5)]}
      />
      <CalendarItem
        title="Bio diversity of high seas"
        variant="light"
        style={[calendarItem5Styles, slideToLeft, delayedAnimation(1.9)]}
      />
      <Card css={[slideToLeft, cardStyles, delayedAnimation(2.3)]}>
        <ScheduleIcon />
        <span>Add to my calendar</span>
      </Card>
    </Wrapper>
  );
};
